"use client";

import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import { Card, CircularProgress } from "@mui/material";
import React, { useMemo } from "react";
import { useNavigate } from "react-router-dom";

import MDBox from "@/components/atoms/MDBox";
import { MDButton } from "@/components/atoms/MDButton";
import MDTypography from "@/components/atoms/MDTypography";
import PageContainer from "@/components/atoms/PageContainer";
import AlertBlock from "@/components/molecules/AlertBlock";
import Suspense from "@/components/molecules/Suspense";
import PageLayout from "@/components/templates/PageLayout";
import { CATEGORIES_PERMISSIONS } from "@/constants/modules.permissions";
import ROUTE_URLS from "@/constants/route-urls";
import useLanguage from "@/contexts/language-context";
import useGetCategories from "@/hooks/requests/category/use-get-categories";
import useHasNoPermission from "@/hooks/use-check-permissions";
import usePagination from "@/hooks/use-pagination";
import ICategory from "@/interfaces/product.category.interfaces";
import { ProductCategoriesMessages } from "@/messages/product-categories/product-categories.messages";

import CategoryTree from "./components/CategoryTree";

type Props = {
  messages: ProductCategoriesMessages;
};

export default function ProductCategoriesPage({ messages }: Props) {
  const router = useNavigate();
  const { lang } = useLanguage();
  const pagination = usePagination();
  const { pageNumber } = pagination;
  const pageSize = 500;

  const { data: categories, isLoading, error } = useGetCategories({ pageNumber, pageSize });

  const uniqueCategories = useMemo(() => {
    if (!categories?.items || categories.items.length === 0) return [];

    const seen = new Set();
    return categories.items.filter((category: ICategory) => {
      const duplicate = seen.has(category.id);
      seen.add(category.id);
      return !duplicate;
    });
  }, [categories?.items]);

  const hasCategories = uniqueCategories && uniqueCategories.length > 0;
  const isCategoriesString = typeof categories === "string";
  const hasError = !!error;

  const loadTable = hasCategories && !isLoading && !hasError;
  const loadError = !hasCategories && !isLoading && hasError;
  const loadNoFound = (!hasCategories && !isLoading && !hasError) || isCategoriesString;
  const hasNoPermissionToCreate = useHasNoPermission(CATEGORIES_PERMISSIONS.CREATE);

  return (
    <PageLayout messages={messages} title={messages.pageTitle}>
      <PageContainer>
        <Card sx={({ functions: { pxToRem } }) => ({ marginTop: pxToRem(16) })}>
          <Suspense isSuspended={isLoading} fallback={<CircularProgress size={50} />}>
            <MDBox p={2}>
              <MDBox p={2} display="flex" justifyContent="space-between" alignItems="center">
                <MDTypography variant="h6">{messages.pageTitle}</MDTypography>

                <MDButton
                  variant="gradient"
                  color="dark"
                  onClick={() => router(ROUTE_URLS.createProductCategory(lang))}
                  disabled={hasNoPermissionToCreate}
                  isLoading={isLoading}
                  endIcon={<AddCircleOutlineIcon />}
                >
                  {messages.addCategoryButton}
                </MDButton>
              </MDBox>

              {!!loadTable && <CategoryTree categories={uniqueCategories} messages={messages} />}

              {!!loadError && (
                <AlertBlock
                  title={messages.errorToLoad}
                  description={error.message}
                  alt={messages.notFoundAlt}
                />
              )}

              {!!loadNoFound && (
                <AlertBlock
                  title={messages.notFoundError}
                  description={messages.notFoundDescription}
                  alt={messages.notFoundAlt}
                />
              )}
            </MDBox>
          </Suspense>
        </Card>
      </PageContainer>
    </PageLayout>
  );
}
